import React, { useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Icon from '@mui/material/Icon';

interface Props {
  ordefulMessage: Record<string, unknown>;
}

interface OrdefulMessage {
  payload: {
    sender: {
      isaId: string;
      name: string;
    };
    receiver: {
      isaId: string;
      name: string;
    };
    id: string;
    controlNumber: string;
    type: {
      name: string;
    };
    businessNumber: string;
    message: {
      interchangeControlHeader: [
        {
          interchangeDate: string;
          interchangeTime: string;
        },
      ];
      transactionSets: [
        {
          N1_loop: [
            {
              partyLocation: [
                {
                  addressInformation: string;
                },
              ];
              geographicLocation: [
                {
                  cityName: string;
                  postalCode: string;
                  countryCode: string;
                  stateOrProvinceCode: string;
                },
              ];
              partyIdentification: [
                {
                  name: string;
                  identificationCode: string;
                  entityIdentifierCode: string;
                  identificationCodeQualifier: string;
                },
              ];
            },
          ];
        },
      ];
    };
  };
}

// Custom JSON viewer to match the pattern used elsewhere in the codebase
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];
type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonObject
  | JsonArray;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    // Expand first level by default
    root: level < 1,
  });

  if (data === null) return <span style={{ color: '#999' }}>null</span>;
  if (data === undefined)
    return <span style={{ color: '#999' }}>undefined</span>;

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#009688',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          &quot;{data}&quot;
        </span>
      );
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>{data ? 'true' : 'false'}</span>
      );
    }
    return <span style={{ wordBreak: 'break-word' }}>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span>[ ]</span>;
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
        {data.length > 1 && (
          <span
            onClick={() =>
              setExpanded((prev) => ({ ...prev, root: !prev.root }))
            }
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {expanded.root ? '▼' : '▶'} Array [{data.length}]
          </span>
        )}

        {data.length === 1 && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}

        {expanded.root && data.length > 1 && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
      {keys.length > 1 && (
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root ? '▼' : '▶'} Object{' '}
          {`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}
        </span>
      )}

      {(expanded.root || keys.length === 1) && (
        <div style={{ paddingLeft: 20, width: '100%' }}>
          {keys.map((key) => (
            <div
              key={key}
              style={{
                wordBreak: 'break-word',
                marginBottom: 2,
                width: '100%',
              }}
            >
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EdiTransactionViewer: React.FC<Props> = ({ ordefulMessage }) => {
  return (
    <MDBox
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden', // Prevent horizontal overflow
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MDTypography variant="h6" mb={1}>
        EDI Transaction Details
      </MDTypography>

      {/* Trading Partner Information */}
      <MDBox mb={1} sx={{ flexShrink: 0 }}>
        <MDBox
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            p: 1,
            pl: 2,
            mb: 1,
          }}
        >
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Sender:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.sender
                ?.isaId || 'N/A'}
              {' (' +
                ((ordefulMessage as unknown as OrdefulMessage)?.payload?.sender
                  ?.name || '??') +
                ')'}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Receiver:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.receiver
                ?.isaId || 'N/A'}
              {' (' +
                ((ordefulMessage as unknown as OrdefulMessage)?.payload
                  ?.receiver?.name || '??') +
                ')'}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Transaction ID:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.id ||
                'N/A'}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Transaction Type:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.type
                ?.name || 'N/A'}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Business Number:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload
                ?.businessNumber || 'N/A'}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              Date/Time:
            </MDTypography>
            <MDTypography variant="caption">
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.message
                ?.interchangeControlHeader?.[0]?.interchangeDate || 'N/A'}{' '}
              {(ordefulMessage as unknown as OrdefulMessage)?.payload?.message
                ?.interchangeControlHeader?.[0]?.interchangeTime || ''}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
          Party Information
        </MDTypography>
        <MDBox
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            p: 2,
            mb: 2,
          }}
        >
          {(
            ordefulMessage as unknown as OrdefulMessage
          )?.payload?.message?.transactionSets?.[0]?.N1_loop?.map(
            (n1Loop, index) => (
              <MDBox
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr',
                  gap: 1,
                  mb: 1,
                }}
              >
                <MDTypography variant="caption" fontWeight="bold">
                  {n1Loop?.partyIdentification?.[0]?.entityIdentifierCode ||
                    'N/A'}
                </MDTypography>
                <MDTypography variant="caption">
                  {n1Loop?.partyIdentification?.[0]?.name || 'N/A'}
                  {' (#'}
                  {n1Loop?.partyIdentification?.[0]?.identificationCode ||
                    'N/A'}
                  {') '}
                  {n1Loop?.partyLocation?.[0]?.addressInformation || 'N/A'}
                  {', '}
                  {n1Loop?.geographicLocation?.[0]?.cityName || 'N/A'}
                  {', '}
                  {n1Loop?.geographicLocation?.[0]?.stateOrProvinceCode ||
                    'N/A'}{' '}
                  {n1Loop?.geographicLocation?.[0]?.postalCode || 'N/A'}
                </MDTypography>
              </MDBox>
            ),
          )}
        </MDBox>
      </MDBox>

      {/* Raw Data Accordion (for technical users) */}
      <MDBox
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <MDBox
          onClick={() => {
            const details = document.getElementById('raw-edi-details');
            if (details) {
              details.style.display =
                details.style.display === 'none' ? 'block' : 'none';
            }
          }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: '4px',
            flexShrink: 0,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Icon sx={{ fontSize: 18 }}>expand_more</Icon>
          <MDTypography variant="caption" color="text.secondary">
            Show Raw EDI Data (Technical)
          </MDTypography>
        </MDBox>

        <MDBox
          id="raw-edi-details"
          sx={{
            display: 'block',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            p: 1,
            backgroundColor: '#fafafa',
            flex: 1, // Take remaining vertical space
            overflow: 'auto', // Make content scrollable
            minHeight: 0, // Allow flex child to shrink
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: 1.4,
          }}
        >
          <JsonViewer data={ordefulMessage.payload as JsonValue} />
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

export default EdiTransactionViewer;
